import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import banner from '../assets/tpp-claims-banner.jpg';
import Layout from '../components/Layout';
import { RootState } from '../store/store';
import { getClaim } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import { useSnackbar } from '../contexts/SnackbarContext';

const buildResumeUrl = (token: string): string => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/?claimToken=${encodeURIComponent(token)}`;
};

const InventoryDraftSaved: React.FC = () => {
  const navigate = useNavigate();
  const claimId = useSelector((state: RootState) => state.claim.claimId);
  const { showError } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!claimId) {
      navigate('/', { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const claim = await getClaim(claimId);
        const token = claim?.token ? String(claim.token) : '';
        if (!cancelled) {
          setResumeUrl(token ? buildResumeUrl(token) : null);
        }
      } catch (e) {
        if (!cancelled) {
          showError(getErrorMessage(e) || 'Unable to load claim.');
          setResumeUrl(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [claimId, navigate, showError]);

  return (
    <Layout>
      <div className="banner">
        <img src={banner} alt="TPP Claims Banner" />
        <div className="banner-content">
          <h1>File a Claim</h1>
          <h2>Tenant Protection Plan</h2>
          <p>Follow our easy-to-use claim process to submit your claim!</p>
        </div>
      </div>

      <Box
        sx={{
          width: '100%',
          maxWidth: { md: 640 },
          mx: 'auto',
          px: { xs: 2, md: 0 },
          py: { xs: 4, md: 6 },
          textAlign: 'center',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: 'var(--primary-color)' }} />
          </Box>
        ) : (
          <>
            <Typography
              component="p"
              sx={{
                margin: 0,
                mb: 2,
                fontSize: { xs: '1.05rem', sm: '1.125rem' },
                fontWeight: 400,
                color: 'var(--text-color)',
                lineHeight: 1.5,
              }}
            >
              Your claim information has been saved.
            </Typography>
            <Typography
              component="p"
              sx={{
                margin: 0,
                mb: 2,
                fontSize: { xs: '1.05rem', sm: '1.125rem' },
                fontWeight: 400,
                color: 'var(--text-color)',
                lineHeight: 1.5,
              }}
            >
              We sent you an email with the link you can use to resume your claim.
            </Typography>
            <Typography
              component="p"
              sx={{
                margin: 0,
                mb: 3.5,
                fontSize: { xs: '1.05rem', sm: '1.125rem' },
                fontWeight: 400,
                color: 'var(--text-color)',
                lineHeight: 1.5,
              }}
            >
              Or you can resume with the button below.
            </Typography>
            {resumeUrl ? (
              <Button
                component="a"
                href={resumeUrl}
                variant="outlined"
                sx={{
                  px: 4,
                  py: 1.25,
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  borderColor: 'var(--primary-color)',
                  color: 'var(--primary-color)',
                  backgroundColor: '#fff',
                  '&:hover': {
                    borderColor: 'var(--primary-color)',
                    backgroundColor: 'rgba(0, 30, 96, 0.04)',
                    color: 'var(--primary-color)',
                  },
                }}
              >
                Resume Your Claim
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Resume link is unavailable. Please use the link from your email.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Layout>
  );
};

export default InventoryDraftSaved;
